import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FileText, Plus, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, absoluteAssetUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import StatusBadge from "../components/StatusBadge";
import { useAuth } from "../lib/auth-context";

const ADMIN_ROLES = new Set(["ORG_ADMIN", "LD_ADMIN"]);

export default function Documents() {
    const nav = useNavigate();
    const { user } = useAuth();
    const isAdmin = ADMIN_ROLES.has(user?.role);
    const [params] = useSearchParams();
    const highlight = params.get("highlight");
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState({});
    const [confirmDoc, setConfirmDoc] = useState(null); // {doc, counts}
    const [confirmStage, setConfirmStage] = useState(0);
    const [confirmType, setConfirmType] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkType, setBulkType] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/documents", { params: q ? { q } : {} });
            setDocs(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    useEffect(() => {
        const anyPending = docs.some((d) => d.status === "UPLOADED" || d.status === "PARSING");
        if (!anyPending) return;
        const t = setInterval(load, 2500);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docs]);

    const openConfirm = async (doc, e) => {
        e?.stopPropagation();
        try {
            const { data } = await api.get(`/documents/${doc.id}/delete-preview`);
            setConfirmDoc({ doc, counts: data.counts });
            setConfirmStage(0); setConfirmType("");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const doDelete = async () => {
        if (!confirmDoc) return;
        setDeleting(true);
        try {
            await api.delete(`/documents/${confirmDoc.doc.id}`);
            toast.success(`Deleted "${confirmDoc.doc.parsed_title || confirmDoc.doc.original_filename}"`);
            setConfirmDoc(null); setConfirmType("");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDeleting(false); }
    };

    const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);

    const bulkDelete = async () => {
        setDeleting(true);
        let ok = 0, fail = 0;
        for (const did of selectedIds) {
            try { await api.delete(`/documents/${did}`); ok++; } catch { fail++; }
        }
        toast[fail ? "warning" : "success"](`Deleted ${ok} documents${fail ? `, ${fail} failed` : ""}`);
        setSelected({}); setBulkOpen(false); setBulkType(""); setDeleting(false);
        await load();
    };

    const toggleAll = () => {
        if (selectedIds.length === docs.length) setSelected({});
        else setSelected(Object.fromEntries(docs.map((d) => [d.id, true])));
    };

    return (
        <div data-testid="documents-page" className="space-y-8">
            <div className="flex items-end justify-between">
                <div>
                    <div className="text-[11px] font-medium uppercase tracking-widest text-neutral-500">Knowledge</div>
                    <h1 className="mt-2 text-3xl font-medium tracking-tight text-neutral-900">Documents</h1>
                    <p className="mt-1.5 text-sm text-neutral-500">
                        Uploaded work instructions and procedures. Click a row to inspect the parsed structure.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {isAdmin && selectedIds.length > 0 && (
                        <Button
                            variant="outline"
                            className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                            onClick={() => { setBulkOpen(true); setBulkType(""); }}
                            data-testid="bulk-delete-docs-btn"
                        >
                            <Trash2 size={14} className="mr-1.5" /> Delete {selectedIds.length}
                        </Button>
                    )}
                    <Input
                        data-testid="documents-search"
                        placeholder="Search…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && load()}
                        className="w-56"
                    />
                    <Button
                        data-testid="documents-upload-btn"
                        onClick={() => nav("/knowledge/upload")}
                        className="bg-neutral-900 text-white hover:bg-neutral-800"
                    >
                        <Plus size={15} className="mr-1.5" /> Upload
                    </Button>
                </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {isAdmin && (
                                <TableHead className="w-10">
                                    <Checkbox
                                        checked={selectedIds.length > 0 && selectedIds.length === docs.length}
                                        onCheckedChange={toggleAll}
                                        data-testid="select-all-docs"
                                    />
                                </TableHead>
                            )}
                            <TableHead className="w-16"></TableHead>
                            <TableHead>Title</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Revision</TableHead>
                            <TableHead>Owner</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Steps</TableHead>
                            <TableHead className="text-right">Images</TableHead>
                            <TableHead>Updated</TableHead>
                            {isAdmin && <TableHead className="w-10"></TableHead>}
                        </TableRow>
                    </TableHeader>
                    <TableBody data-testid="documents-table-body">
                        {loading && (
                            <TableRow><TableCell colSpan={isAdmin ? 11 : 9} className="text-center text-sm text-neutral-500">Loading…</TableCell></TableRow>
                        )}
                        {!loading && docs.length === 0 && (
                            <TableRow><TableCell colSpan={isAdmin ? 11 : 9} className="py-12 text-center text-sm text-neutral-500">
                                No documents yet. Upload one to get started.
                            </TableCell></TableRow>
                        )}
                        {docs.map((d) => (
                            <TableRow
                                key={d.id}
                                data-testid={`document-row-${d.id}`}
                                className={`cursor-pointer hover:bg-neutral-50 ${highlight === d.id ? "bg-blue-50/40" : ""}`}
                            >
                                {isAdmin && (
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                            checked={!!selected[d.id]}
                                            onCheckedChange={(v) => setSelected((prev) => ({ ...prev, [d.id]: !!v }))}
                                            data-testid={`select-doc-${d.id}`}
                                        />
                                    </TableCell>
                                )}
                                <TableCell onClick={() => nav(`/knowledge/documents/${d.id}`)}>
                                    {d.thumbnail_signed_url ? (
                                        <img src={absoluteAssetUrl(d.thumbnail_signed_url)} alt="" className="h-10 w-10 rounded-md border border-neutral-200 object-cover" />
                                    ) : (
                                        <div className="flex h-10 w-10 items-center justify-center rounded-md border border-neutral-200 bg-neutral-50 text-neutral-400">
                                            <FileText size={16} />
                                        </div>
                                    )}
                                </TableCell>
                                <TableCell onClick={() => nav(`/knowledge/documents/${d.id}`)}>
                                    <div className="font-medium text-neutral-900">{d.parsed_title || d.original_filename}</div>
                                    <div className="mono text-[11px] text-neutral-500">{d.parsed_doc_number || d.original_filename}</div>
                                </TableCell>
                                <TableCell onClick={() => nav(`/knowledge/documents/${d.id}`)} className="text-neutral-700">{d.type}</TableCell>
                                <TableCell onClick={() => nav(`/knowledge/documents/${d.id}`)} className="mono text-xs text-neutral-600">{d.parsed_revision || "—"}</TableCell>
                                <TableCell onClick={() => nav(`/knowledge/documents/${d.id}`)} className="text-neutral-600">{d.owner_name || "—"}</TableCell>
                                <TableCell onClick={() => nav(`/knowledge/documents/${d.id}`)}>
                                    <StatusBadge status={d.status} testid={`status-${d.id}`} />
                                    {d.status === "FAILED" && d.parse_error && (
                                        <div className="mt-1 max-w-xs text-[11px] text-red-600 truncate" title={d.parse_error}>{d.parse_error}</div>
                                    )}
                                </TableCell>
                                <TableCell onClick={() => nav(`/knowledge/documents/${d.id}`)} className="text-right mono text-sm">{d.step_count}</TableCell>
                                <TableCell onClick={() => nav(`/knowledge/documents/${d.id}`)} className="text-right mono text-sm">{d.media_count}</TableCell>
                                <TableCell onClick={() => nav(`/knowledge/documents/${d.id}`)} className="mono text-xs text-neutral-500">{new Date(d.updated_at).toLocaleString()}</TableCell>
                                {isAdmin && (
                                    <TableCell className="text-right">
                                        <Button
                                            variant="ghost" size="sm"
                                            onClick={(e) => openConfirm(d, e)}
                                            className="text-neutral-400 hover:text-red-700 hover:bg-red-50"
                                            data-testid={`delete-doc-${d.id}`}
                                        >
                                            <Trash2 size={14} />
                                        </Button>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* Two-step confirm modal for single doc */}
            <Dialog open={!!confirmDoc} onOpenChange={(v) => !v && setConfirmDoc(null)}>
                <DialogContent className="sm:max-w-lg" data-testid="delete-doc-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-700">
                            <AlertTriangle size={18} /> Delete this document?
                        </DialogTitle>
                        <DialogDescription>
                            Everything derived from it goes too: parsed steps, images, and any training
                            module built from it (including its scenes, renders, and quizzes). This cannot
                            be undone.
                        </DialogDescription>
                    </DialogHeader>
                    {confirmDoc && (
                        <div className="space-y-3 text-sm">
                            <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3">
                                <div className="font-medium text-neutral-900">{confirmDoc.doc.parsed_title || confirmDoc.doc.original_filename}</div>
                                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] text-neutral-600">
                                    <div>Procedure steps: <span className="mono font-medium">{confirmDoc.counts.procedure_steps}</span></div>
                                    <div>Media assets: <span className="mono font-medium">{confirmDoc.counts.media_assets}</span></div>
                                    <div>Versions: <span className="mono font-medium">{confirmDoc.counts.document_versions}</span></div>
                                    <div className="text-red-700">Modules built from: <span className="mono font-medium">{confirmDoc.counts.modules_built_from}</span></div>
                                </div>
                            </div>
                            {confirmStage === 0 ? (
                                <Button
                                    variant="outline" className="w-full border-red-200 text-red-700 hover:bg-red-50"
                                    onClick={() => setConfirmStage(1)}
                                    data-testid="delete-doc-continue"
                                >
                                    I understand — continue
                                </Button>
                            ) : (
                                <>
                                    <div className="text-xs text-neutral-600">Type <span className="mono font-semibold">DELETE</span> to confirm.</div>
                                    <Input value={confirmType} onChange={(e) => setConfirmType(e.target.value)} placeholder="DELETE" autoFocus data-testid="delete-doc-confirm-input" />
                                    <Button onClick={doDelete} disabled={confirmType !== "DELETE" || deleting} className="w-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-40" data-testid="delete-doc-confirm-btn">
                                        {deleting ? "Deleting…" : "Delete permanently"}
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-700">
                            <AlertTriangle size={18} /> Delete {selectedIds.length} documents?
                        </DialogTitle>
                        <DialogDescription>
                            Every parsed step, image, and module built from these documents will be
                            permanently removed. This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div className="text-xs text-neutral-600">Type <span className="mono font-semibold">DELETE</span> to confirm.</div>
                        <Input value={bulkType} onChange={(e) => setBulkType(e.target.value)} placeholder="DELETE" autoFocus data-testid="bulk-doc-confirm-input" />
                        <Button onClick={bulkDelete} disabled={bulkType !== "DELETE" || deleting} className="w-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-40" data-testid="bulk-doc-confirm-btn">
                            {deleting ? "Deleting…" : "Delete permanently"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
